// src/pages/training-videos-library/components/AddVideoModal.jsx
// Modal form to add a new training video (YouTube link) to Supabase.
// Includes live YouTube preview, auto-filled thumbnail, duration fetch.

import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase, extractYouTubeId, getYouTubeThumbnail } from '../../../lib/supabase';

// ─── tiny debounce ────────────────────────────────────────────
function useDebounce(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── YouTube oEmbed: fetch title + thumbnail ──────────────────
async function fetchYouTubeMeta(ytId) {
  if (!ytId) return null;
  try {
    const res = await fetch(
      `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${ytId}&format=json`
    );
    if (!res.ok) return null;
    return await res.json(); // { title, thumbnail_url, ... }
  } catch {
    return null;
  }
}

const CATEGORY_ICONS = {
  Procedures:     'Stethoscope',
  Equipment:      'Wrench',
  Safety:         'Shield',
  Software:       'Monitor',
  Clinical:       'Activity',
  Administration: 'FileText',
};

// ─────────────────────────────────────────────────────────────
const AddVideoModal = ({ onClose, onSuccess }) => {
  // Form fields
  const [youtubeUrl, setYoutubeUrl]     = useState('');
  const [title, setTitle]               = useState('');
  const [description, setDescription]   = useState('');
  const [categoryId, setCategoryId]     = useState('');
  const [durationSecs, setDurationSecs] = useState('');
  const [isNew, setIsNew]               = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState('');

  // UI state
  const [categories, setCategories]   = useState([]);
  const [ytId, setYtId]               = useState(null);
  const [preview, setPreview]         = useState(null);   // { title, thumbnail_url }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [errors, setErrors]           = useState({});
  const [step, setStep]               = useState(1);      // 1 = URL, 2 = details

  const debouncedUrl = useDebounce(youtubeUrl, 600);
  const titleRef     = useRef(null);

  // Load categories from Supabase
  useEffect(() => {
    supabase
      .from('video_categories')
      .select('id, slug, name')
      .eq('is_active', true)
      .order('name')
      .then(({ data }) => {
        if (data) setCategories(data);
      });
  }, []);

  // Auto-detect YouTube ID + fetch meta whenever URL changes
  useEffect(() => {
    const id = extractYouTubeId(debouncedUrl);
    setYtId(id);
    if (!id) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    fetchYouTubeMeta(id).then((meta) => {
      setPreview(meta);
      if (meta) {
        // Auto-fill title if blank
        if (!title) setTitle(meta.title || '');
        setThumbnailUrl(meta.thumbnail_url || getYouTubeThumbnail(id) || '');
      }
      setPreviewLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUrl]);

  // Validate
  const validate = () => {
    const e = {};
    if (!ytId)        e.youtubeUrl   = 'URL YouTube invalide';
    if (!title.trim()) e.title       = 'Le titre est requis';
    if (!categoryId)   e.categoryId  = 'Choisissez une catégorie';
    if (durationSecs && isNaN(Number(durationSecs))) e.durationSecs = 'Durée invalide (secondes)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);

    const { data: { user } } = await supabase.auth.getUser();

    const payload = {
      title:            title.trim(),
      description:      description.trim() || null,
      youtube_url:      youtubeUrl.trim(),
      youtube_id:       ytId,
      thumbnail_url:    thumbnailUrl || getYouTubeThumbnail(ytId) || null,
      duration_seconds: durationSecs ? parseInt(durationSecs, 10) : 0,
      category_id:      categoryId || null,
      is_new:           isNew,
      is_active:        true,
      uploaded_by:      user?.id || null,
    };

    const { error } = await supabase.from('training_videos').insert(payload);
    setSaving(false);

    if (error) {
      setErrors({ submit: error.message });
    } else {
      onSuccess?.();
      onClose?.();
    }
  };

  // Close on Escape
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  const isUrlValid = Boolean(ytId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Youtube" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Ajouter une vidéo</h2>
              <p className="text-xs text-muted-foreground">Lien YouTube uniquement</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-6 pt-4">
          {[1, 2].map((s) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-1.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                  step >= s
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {step > s ? <Icon name="Check" size={12} /> : s}
                </div>
                <span className={`text-xs font-medium ${step >= s ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {s === 1 ? 'Lien YouTube' : 'Détails'}
                </span>
              </div>
              {s < 2 && (
                <div className={`flex-1 h-px ${step > s ? 'bg-primary' : 'bg-border'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">

          {/* ── STEP 1: YouTube URL ── */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  URL YouTube <span className="text-error">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Icon name="Link" size={16} color="var(--color-muted-foreground)" />
                  </div>
                  <input
                    type="url"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className={`w-full pl-9 pr-10 py-2.5 bg-background border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                      errors.youtubeUrl
                        ? 'border-error focus:ring-error/30'
                        : isUrlValid
                        ? 'border-success'
                        : 'border-border'
                    }`}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {previewLoading && (
                      <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    )}
                    {!previewLoading && isUrlValid && (
                      <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                    )}
                    {!previewLoading && youtubeUrl && !isUrlValid && (
                      <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                    )}
                  </div>
                </div>
                {errors.youtubeUrl && (
                  <p className="text-xs text-error mt-1">{errors.youtubeUrl}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Formats acceptés : youtube.com/watch?v=... · youtu.be/... · /shorts/...
                </p>
              </div>

              {/* Preview card */}
              {isUrlValid && preview && (
                <div className="flex gap-3 p-3 bg-muted/40 border border-border rounded-xl">
                  <img
                    src={preview.thumbnail_url || getYouTubeThumbnail(ytId)}
                    alt="thumbnail"
                    className="w-28 h-20 object-cover rounded-lg flex-shrink-0 bg-muted"
                  />
                  <div className="flex-1 min-w-0 py-0.5">
                    <p className="text-sm font-medium text-foreground line-clamp-2 leading-snug">
                      {preview.title}
                    </p>
                    <div className="flex items-center gap-1.5 mt-2">
                      <Icon name="Youtube" size={12} color="var(--color-error)" />
                      <span className="text-xs text-muted-foreground">ID: {ytId}</span>
                    </div>
                    <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 bg-success/10 text-success text-xs rounded-full">
                      <Icon name="CheckCircle2" size={11} />
                      Vidéo détectée
                    </div>
                  </div>
                </div>
              )}

              {/* Loading preview skeleton */}
              {previewLoading && (
                <div className="flex gap-3 p-3 bg-muted/40 border border-border rounded-xl animate-pulse">
                  <div className="w-28 h-20 bg-muted rounded-lg flex-shrink-0" />
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-3 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: Details ── */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Titre <span className="text-error">*</span>
                </label>
                <input
                  ref={titleRef}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Introduction aux aligneurs ClearMotion"
                  className={`w-full px-3 py-2.5 bg-background border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                    errors.title ? 'border-error' : 'border-border'
                  }`}
                />
                {errors.title && <p className="text-xs text-error mt-1">{errors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Décrivez le contenu de cette vidéo..."
                  rows={3}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all resize-none"
                />
              </div>

              {/* Category + Duration row */}
              <div className="grid grid-cols-2 gap-4">
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Catégorie <span className="text-error">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setCategoryId(cat.id)}
                        className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg border text-xs font-medium transition-all ${
                          categoryId === cat.id
                            ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                            : 'bg-background border-border text-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon
                          name={CATEGORY_ICONS[cat.name] || 'Folder'}
                          size={13}
                        />
                        <span className="truncate">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                  {errors.categoryId && (
                    <p className="text-xs text-error mt-1">{errors.categoryId}</p>
                  )}
                </div>

                {/* Right column: Duration + isNew */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Durée (secondes)
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                        <Icon name="Clock" size={14} color="var(--color-muted-foreground)" />
                      </div>
                      <input
                        type="number"
                        min={0}
                        value={durationSecs}
                        onChange={(e) => setDurationSecs(e.target.value)}
                        placeholder="Ex: 1245"
                        className={`w-full pl-8 pr-3 py-2.5 bg-background border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                          errors.durationSecs ? 'border-error' : 'border-border'
                        }`}
                      />
                    </div>
                    {durationSecs > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        ≈ {Math.floor(durationSecs / 60)}min {durationSecs % 60}s
                      </p>
                    )}
                    {errors.durationSecs && (
                      <p className="text-xs text-error mt-1">{errors.durationSecs}</p>
                    )}
                  </div>

                  {/* Thumbnail override */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Miniature personnalisée
                    </label>
                    <input
                      type="url"
                      value={thumbnailUrl}
                      onChange={(e) => setThumbnailUrl(e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                    />
                  </div>

                  {/* Is New badge toggle */}
                  <div
                    onClick={() => setIsNew((p) => !p)}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer select-none transition-all ${
                      isNew
                        ? 'bg-error/10 border-error/30 text-error'
                        : 'bg-background border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                      isNew ? 'bg-error border-error' : 'border-border'
                    }`}>
                      {isNew && <Icon name="Check" size={10} color="white" />}
                    </div>
                    <span className="text-xs font-medium">Marquer comme NOUVEAU</span>
                  </div>
                </div>
              </div>

              {/* Thumbnail preview */}
              {thumbnailUrl && (
                <div className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg border border-border">
                  <img
                    src={thumbnailUrl}
                    alt="preview"
                    className="w-20 h-14 object-cover rounded bg-muted flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <p className="text-xs text-muted-foreground">Aperçu de la miniature</p>
                </div>
              )}

              {/* Submit error */}
              {errors.submit && (
                <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                  <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
                  <p className="text-sm text-error">{errors.submit}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/20">
          <button
            onClick={step === 1 ? onClose : () => setStep(1)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name={step === 1 ? 'X' : 'ArrowLeft'} size={16} />
            {step === 1 ? 'Annuler' : 'Retour'}
          </button>

          {step === 1 ? (
            <Button
              variant="default"
              iconName="ArrowRight"
              iconPosition="right"
              disabled={!isUrlValid}
              onClick={() => {
                setErrors({});
                setStep(2);
                setTimeout(() => titleRef.current?.focus(), 100);
              }}
            >
              Continuer
            </Button>
          ) : (
            <Button
              variant="default"
              iconName={saving ? 'Loader' : 'Plus'}
              iconPosition="left"
              disabled={saving}
              onClick={handleSubmit}
            >
              {saving ? 'Enregistrement...' : 'Ajouter la vidéo'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddVideoModal;